import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './WeblinksEditor.html?raw';
import _StatefulSection from '../../moreCore/UI/_StatefulSection/_StatefulSection';
import ExpandableSection from '../../moreCore/UI/Container/ExpandableSection';
import StaticTable from '../../moreCore/UI/StaticTable/StaticTable';
import EntityStore from '../../moreCore/Tools/EntityStore';
import lang from 'dojo/_base/lang';
import _uiFrameMixin from '../../moreCore/UI/Container/uiFrame';
import { ExpandableSectionMarkings } from "../../moreCore/UI/Container/ExpandableSectionTypes";
import CheckBox from '../../moreCore/UI/CheckBox/CheckBox';
import _GlobalSelectEntityDialogsMixin from '../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import { HTWebfileLinkCategory, WebfileLink, WebfileLinkCategory, WeblinkType } from "./WeblinksEditorTypes";
import { SelectEntityDialogTypes } from "../../moreCore/_loadingHelpers/SelectEntityDialog";
import HintBox from '../../moreCore/UI/HintBox/HintBox';
import SelectBox from '../../moreCore/UI/SelectBox/SelectBox';
import SearchSelect from '../../moreCore/UI/SearchSelect/SearchSelect';

class WeblinksEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private categoryTableExpander: MdsLax<InstanceType<typeof ExpandableSection>>;
    private categoryStore: MdsLax<InstanceType<typeof EntityStore<HTWebfileLinkCategory>>>;
    private categoryTable: MdsLax<InstanceType<typeof StaticTable>>;

    private mainCategoryCheckbox: MdsLax<InstanceType<typeof CheckBox>>;
    private mainCategoryEntity: HTWebfileLinkCategory;

    private mainEntityId: string;

    private placementSelect: MdsLax<InstanceType<typeof SearchSelect>>;
    private mainCategoryHintbox: MdsLax<InstanceType<typeof HintBox>>;

    private isNew: boolean;
    private isExistingWeblink: boolean;

    private typeSelect: MdsLax<InstanceType<typeof SelectBox>>;
    private type: WeblinkType;

    private types = [
        {
            label: (this as any).translate("General_Label_WebfileLinkAppointment"),
            value: "webfile_link_appointment"
        },
        {
            label: (this as any).translate("General_Label_WebfileLinkGeneral"),
            value: "webfile_link_general"
        },
    ]

    private rowConfig: StaticTable.RowConfig = {
        attributes: [
            "ref_webfile_link_category.description",
            "is_active",
            "is_main_webfile_link_category"
        ],
        headings: [
            (this as any).translate("General_Description"),
            (this as any).translate("General_Label_IsActive"),
            (this as any).translate("General_Label_WebfileMainCategory")
        ],
        colNamesInternal: [
            "description",
            "is_active",
            "is_main_category"
        ]
    } 

    private tableLayout: StaticTable.Layout = {
        label: "categoryTable",
        name: "categoryTable",
        rows: [(this as any).rowConfig]
    }

    setup(data: WebfileLink, id: string, isNew: boolean = false) {
        if (!data) {
            console.error("Error: Data empty");
            return;
        }

        console.log(data);

        this.isNew = isNew;
        this.setData(data);

        this.mainEntityId = id;

        this.isExistingWeblink = data.Classname === "webfile_link_appointment" || data.Classname === "webfile_link_general";
        this.hideNode(this.typeSelect, this.isExistingWeblink);
        this.setAttributeValue("isNewWeblink", !this.isExistingWeblink, true);

        this.setType(data.Classname);
        let heading = data.Classname ? `${this.translate("General_Heading_WeblinkDLG")}: ${data.$Displayname}` : this.translate("General_Heading_NewWeblinkDLG");

        this.typeSelect.setData(this.type, this.types, "value", "label");
        this.setAttributeValue("ClassPrintname", this.typeSelect.getValue(true), true);
        this.setAttributeValue("Classname", this.typeSelect.getValue(), true);

        this.show("dlg", heading);
        this.createDefaultButton("ok", lang.hitch(this, this._onClickConfirm));
    }

    private setType(type: WeblinkType) {
        this.type = type;

        switch (type) {
            case "webfile_link_appointment":
            default:
                this.type = "webfile_link_appointment";
                this.hideNode(this.placementSelect, false);
                break;
            case "webfile_link_general":
                this.hideNode(this.placementSelect, true);
                break;
        }
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {
            case "type":
                this.setType(value);
                console.log(this.typeSelect.getValue(true));
                this.setAttributeValue("Classname", value);
                this.setAttributeValue("ClassPrintname", this.typeSelect.getValue(true), true);
                break;
        }
    }

    protected onShow() {
        this._uiFrameDlg.enableContentOverflow(true);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        console.log(this.getCurrentData());

        let categories = this.getAttributeValue("EL_ht_webfile_link_category_webfile_link");
        this.categoryStore.setup(categories);
        this.categoryTableExpander.setMarking(categories?.length > 0 ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.default);
        if (categories?.length > 0) this.categoryTableExpander.toggleExpand(true);
    }

    private _onClickConfirm(e): void {
        if (!this.mainCategoryHintbox.getValue("entity")) {
            this.showToast(this.translate("WeblinksEditor_Error_EmptyMainCategory"), "fas fa-exclamation-triangle");
            return;
        }

        let data = this.getCurrentData();
        this.emit("confirmed", { entity: data, mainEntityId: this.mainEntityId, isNew: this.isNew });
        this.close();
    }

    private _onClickNew(e): void {
        this.loadSelectEntityDialog(e, "selectCategory", lang.hitch(this, this._onSEDCallback));
    }

    private _onClickDelete(rowGroup: StaticTable.StaticTableRowGroup<HTWebfileLinkCategory>): void {
        if (rowGroup.entity.is_main_webfile_link_category) {
            this.mainCategoryHintbox.setValue(null);
        }
    }

    private _onSEDCallback(mode: string, data: Array<string>, items: Array<SelectEntityDialogTypes.Item<WebfileLinkCategory>>) {
        switch (mode) {
            case "selectCategory":
                if (items && items.length > 0) {
                    let category = items[0].entry.data;
                    if (this.storeHasCategory(category)) {
                        this.showToast(this.translate("WeblinksEditor_Error_CategoryInStore"), "fas fa-exclamation-triangle");
                        return;
                    }

                    let newCategory: HTWebfileLinkCategory = {
                        is_active: true,
                        is_main_webfile_link_category: this.categoryStore.getCurrentCompleteList().length > 0 ? false : true,
                        ref_webfile_link_category: category,
                        ID: "" + Math.random() * 1000000,
                        $Displayname: "",
                        Classname: "ht_webfile_link_category",
                        ClassPrintname: ""
                    }
                    this.categoryStore.addEntry(newCategory);

                    if (newCategory.is_main_webfile_link_category) {
                        this.setAttributeValue("ref_main_ht_webfile_link_category.ref_webfile_link_category", newCategory.ref_webfile_link_category, true);
                        this.mainCategoryHintbox.setValue(newCategory.ref_webfile_link_category);
                    }
                }
                break;
        }
    }

    private storeHasCategory(category: WebfileLinkCategory): boolean {
        let completeList: Array<HTWebfileLinkCategory> = this.categoryStore.getCurrentCompleteList();

        for (let storeCategory of completeList) {
            if (storeCategory.ref_webfile_link_category.ID === category.ID) return true;
        }
        return false;
    } 

    private tableFormatter(cell: StaticTable.StaticTableCell<HTWebfileLinkCategory>) {
        let node = cell.contentnode;
        let entity = cell.entity;
        let colName = cell.name;
        let value = cell.value;
        let rowIndex = cell.tableRow.rowGroup.rowIndex;

        switch (colName) {
            case "is_active":
                let isActiveCheckbox = new CheckBox();
                isActiveCheckbox.on("change", lang.hitch(this, this._onChangeIsActiveCheckbox, entity));
                isActiveCheckbox.setValue(value, true);
                isActiveCheckbox.placeAt(node);

                return true;
                break;
            case "is_main_category":
                let isMainCategoryCheckbox = new CheckBox();
                isMainCategoryCheckbox.on("change", lang.hitch(this, this._onChangeIsMainCategoryCheckbox, entity, isMainCategoryCheckbox));
                isMainCategoryCheckbox.setValue(value, true);
                isMainCategoryCheckbox.placeAt(node);

                if (value) {
                    this.mainCategoryCheckbox = isMainCategoryCheckbox;
                    this.mainCategoryEntity = entity;
                }

                return true;
                break;
        }

        return false;
    }

    private _onChangeIsMainCategoryCheckbox(entity: HTWebfileLinkCategory, checkbox: MdsLax<InstanceType<typeof CheckBox>>, e) {
        if (e) {
            entity.is_main_webfile_link_category = e;
            this.categoryStore.changeEntry(entity, true);
            this.setAttributeValue("ref_main_ht_webfile_link_category.ref_webfile_link_category", entity.ref_webfile_link_category);

            this.mainCategoryCheckbox.setValue(!e, true);
            this.mainCategoryEntity.is_main_webfile_link_category = false;
            this.categoryStore.changeEntry(this.mainCategoryEntity, true);

            this.mainCategoryCheckbox = checkbox;
            this.mainCategoryEntity = entity;
        } else {
            checkbox.setValue(!e, true);
        }
    }

    private _onChangeIsActiveCheckbox(entity: HTWebfileLinkCategory, e: boolean) {
        entity.is_active = e;
        this.categoryStore.changeEntry(entity, true);
    }

    private _onChangeStore() {
        this.setAttributeValue("categoryDiffData", this.categoryStore.getDiffData());
        this.setAttributeValue("EL_ht_webfile_link_category_webfile_link", this.categoryStore.getCurrentCompleteList());
    }


    private setupTable() {
        this.categoryTable = new StaticTable();
        this.categoryTable.setLayout(this.tableLayout);
        this.categoryTable.setFormatter(lang.hitch(this, this.tableFormatter));
        this.categoryTable.setupFromStore(this.categoryStore);
        this.categoryTable.addDefaultRowActionButton("delete", lang.hitch(this, this._onClickDelete));
        this.categoryTable.setCyAttribute("WeblinksEditorCategoryTable");

        this.categoryTable.placeAt(this.categoryTableExpander);
    }


    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.placementSelect.setReadonly(true);

        this.categoryStore = new EntityStore();
        this.categoryStore.on("change", lang.hitch(this, this._onChangeStore));

        this.setupTable();

        this.SEDconfigCallfunction = "Webtool_API__Weblinks_SED_GetConfig";
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(WeblinksEditor);